<!-- Quiz.vue -->
<template>
    <!-- Grading Notes (shows BEFORE attempt is submitted) -->
    <div class="flex flex-col gap-14 pb-14" v-if="!completed && gradingNote">
        <article class="flex flex-col gap-14">
            <div class="richtext" v-html="gradingNote"></div>
        </article>
    </div>

    <section
        class="flex flex-col gap-14 transition-opacity duration-200"
        :class="{ 'opacity-50 pointer-events-none': isSubmitting }"
        v-if="!isLoading"
    >
        <!-- Submit Quiz Attempt return value -->
        <div class="flex flex-col gap-14" v-if="completed">
            <!-- Grades -->
            <QuizGrade
                v-if="graded"
                :letter-grade="grade.letterGrade"
                :percent="grade.percent"
                :quiz-passed="grade.quizPassed"
                :certificate-url="grade.certificateUrl ?? certificateUrl"
                :first-lesson-url="firstLessonUrl"
                :quiz-id="quizId"
                :user-id="userId"
                :course-passed-copy="coursePassedCopy"
                :course-failed-copy="courseFailedCopy"
                @reload-quiz="reloadQuiz"
                @set-submitting="setSubmitting"
            />

            <!-- Results -->
            <section class="flex flex-col gap-12" id="quiz-results">
                <h3 class="heading-03">Review Correct Answers</h3>

                <article class="flex flex-col gap-14">
                    <ol class="marker:font-semibold flex flex-col gap-14 pl-6 list-decimal text-lg ">
                        <li v-for="(q, index) in results" :key="index" class="pb-2">
                            <QuizResult
                                :question="q"
                                :attempt="attempt"
                                :graded="true"
                            />
                        </li>
                    </ol>
                </article>
            </section>
        </div>

        <!-- Submit Quiz Attempt Form -->
        <section class="flex flex-col gap-14" v-else>
            <ol class="marker:font-semibold flex flex-col gap-14 pl-6 list-decimal text-lg">
                <li v-for="(q, index) in questions" :key="index" class="pl-6">
                    <QuizQuestion
                        v-if="graded || !checkIfAnswered(q)"
                        :question="q"
                        @select-answer="selectAnswer"
                    />

                    <!-- Instant question result, just for ungraded quizzes -->
                    <QuizResult
                        v-if="!graded && checkIfAnswered(q)"
                        :question="q"
                        :attempt="attempt"
                        :graded="graded"
                    />
                </li>
            </ol>

            <div class="flex justify-center" v-if="graded">
                <button class="button button--rounded button--large" type="button" @click="submitQuizAttempt">Submit Answers</button>
            </div>
        </section>
    </section>
</template>
<style>
    [v-cloak] {
        display: none;
    }
</style>

<script setup>
    import { ref, onMounted, nextTick, computed } from 'vue';
    import axios from 'axios';
    import QuizQuestion from './quiz/QuizQuestion.vue';
    import QuizGrade from './quiz/QuizGrade.vue';
    import QuizResult from './quiz/QuizResult.vue';
    import VideoPlayers from '../utils/videoPlayers.js';
    import audioPlayer from '../utils/audioPlayer.js';

    const props = defineProps({
        quizId: null,
        url: null,
        userId: null,
        graded: true,
        coursePassedCopy: '',
        courseFailedCopy: '',
        certificateUrl: '',
        gradingNote: ''
    });

    const questions = ref([]);
    const attempt = ref([]);
    const results = ref(null);
    const grade = ref({
        letterGrade: '',
        percent: '',
        quizPassed: false,
        certificateUrl: '',
    });
    const attemptType = ref('attempt');
    const completed = ref(false);
    const token = ref(null);
    const isSubmitting = ref(false);
    const isLoading = ref(true);
    const firstLessonUrl = ref('');
    const quizComplete = ref(false);

    function shuffle(array) {
        let currentIndex = array.length;

        // While there remain elements to shuffle...
        while (currentIndex != 0) {

            // Pick a remaining element...
            let randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex--;

            // And swap it with the current element.
            [array[currentIndex], array[randomIndex]] = [
                array[randomIndex], array[currentIndex]
            ];
        }
    }

    const getQuiz = async () => {
        isLoading.value = true;
        attempt.value = []; // Clear previous attempts

        try {
            const endpoint = `${props.url}courses/get-quiz`;
            const response = await axios.get(endpoint, { params: { 'quizId': props.quizId, 'userId': props.userId } });
            const data = response.data;

            firstLessonUrl.value = response.data['firstLessonUrl'] ?? ''; 

            if(data.courseId) {
                processResults(data);
            } else {
                // If the Quiz is Graded, shuffle the Questions
                let orderedQuestions = response.data.questions;
                if (props.graded) {
                    shuffle(orderedQuestions);
                }

                questions.value = orderedQuestions;
                completed.value = false; // Ensure completed is set to false when loading new questions
            }
        } catch (error) {
            console.error('Error fetching quiz:', error);
        } finally {
            isLoading.value = false;
            isSubmitting.value = false; // Ensure submitting state is reset

            // Remove the skeleton after the quiz has loaded if it exists
            setTimeout(() => {
                const skeleton = document.querySelector('.js-quiz-skeleton');
                if (skeleton) {
                    skeleton.remove();
                }

                // Initialize Video Players after Quiz has loaded
                VideoPlayers.init();
                VideoPlayers.parseCkEditorOembed();
                audioPlayer.init();
            }, 150);
        }
    };

    const getToken = async () => {
        const response = await axios.get('/actions/blitz/csrf/token');
        token.value = response.data;
    };

    const processResults = function(data) {
        //console.log(data);
        grade.value = {
            letterGrade: data['letterGrade'],
            percent: data['percentage'],
            quizPassed: data['quizPassed'],
            certificateUrl: data['certificateUrl']
        };

        results.value = data['quizQuestions'];
        attempt.value = data['answerAttempts'];

        completed.value = true;
        isSubmitting.value = false; // Ensure submitting state is reset
    }

    const submitQuizAttempt = async () => {
        isSubmitting.value = true;

        const params = {
            'quizId': props.quizId,
            'userId': props.userId,
            'answerAttempts': attempt.value,
            'attemptType': attemptType.value,
            'CRAFT_CSRF_TOKEN': token.value
        };
        
        const endpoint = `${props.url}courses/submit-quiz-attempt`;
        const config = {
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/json',
                'Content-Type': 'multipart/form-data',
                'Access-Control-Allow-Origin': props.url,
                'X-CSRF-Token': ''
            }
        };

        const response = await axios.post(endpoint, params, config);
        const data = response.data;

        processResults(data);
        quizComplete.value = true;

        // If the Quiz is Passed, update the course progress sidebar by
        // emitting an event that the Course.js file can hear and use
        if(data.quizPassed) {
            const event = new CustomEvent('quizPassed', {
                detail: {
                    completedPageIds: data.completedPageIds ?? [],
                    progressEntryId: data.progressEntryId ?? null,
                    hasPassedQuiz: data.hasPassedQuiz ?? data.quizPassed
                }
            });
            document.dispatchEvent(event);
        }

        // Scroll to the top of the page
        const stream = document.getElementById('stream');
        if (stream) {
            stream.scrollIntoView({ behavior: 'smooth' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // When an Answer is selected, replace the previously-selected
    // Answer for the Question, if one exists, and add the selection
    // to the "Attempt" array for Grading
    const selectAnswer = (qId, aId) => {
        const selection = {
            'questionId': qId,
            'answers': [
                {'answerId': aId}
            ]
        };

        // Check if this selection should replace a previous one for this Question
        let previousSelectionIndex = null;
        let previousSelection = attempt.value.find((item, index) => {
            //console.log(`attempt | index ${index} | question: ${item.questionId} | answer: ${item.answers[0].answerId}`);
            if(item.questionId == qId) {
                previousSelectionIndex = index;
                return true;
            }
        });

        if(previousSelection) {
            attempt.value[previousSelectionIndex]['answers'][0]['answerId'] = aId;
        } else {
            attempt.value.push(selection);
        }
    };

    const checkIfAnswered = computed(() => (question) => {
        const qId = question.questionId;
        return attempt.value.some(attempt => attempt.questionId === qId);
    });

    // Function to reload the quiz component
    const setSubmitting = (value) => {
        isSubmitting.value = value;
    };
    
    const reloadQuiz = () => {
        //console.log('Reloading quiz...');

        // When the user Retakes the Quiz, update the course progress sidebar by
        // emitting an event that the Course.js file can hear and use
        if(grade.value.quizPassed) {
            const event = new Event('quizReset');
            document.dispatchEvent(event);
        }

        getQuiz();
    };

    onMounted(() => {
        //console.log('quiz');
        getQuiz();
        getToken();
    });

    nextTick(() => {
        VideoPlayers.init();
    });
</script>
